import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';
import {WebsocketEnums} from "../../enums/websocket-enums";
import { isPlatformBrowser } from '@angular/common';

interface MessageReactionEvent {
  userId: number;
  reaction: string;
  // Add other relevant fields
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketsHelperService {
  newMessageSubject = new Subject<any>();
  newChatSubject = new Subject<any>();
  refreshListSubject = new Subject<any>();
  newEmojiReaction = new Subject<MessageReactionEvent>();
  deleteEmojiReaction = new Subject<MessageReactionEvent>();
  messageUpdated = new Subject<any>();
  messageDeleted = new Subject<any>();
  userLeft = new Subject<any>();
  hasUserBeenRemoved = new Subject();
  groupDetailsChanges = new Subject();
  notificationReceivedSubject = new Subject();
  private channels: Map<number, any> = new Map();
  private activeListeners: Set<string> = new Set();

  constructor(@Inject(PLATFORM_ID) private platformId: string,){}

  // Method to handle common Echo instance checks
  private getEchoInstance(): any | null {
    const echoInstance = (window as any)?.Echo;
    if (!echoInstance) {
      return null; // Early exit to prevent runtime errors
    }
    // Consider storing socketId only once if necessary
    if (isPlatformBrowser(this.platformId)) {
      if (echoInstance) {
        echoInstance.connector.pusher.connection.bind('connected', () => {
          localStorage.setItem('socketId', echoInstance.socketId());
        });
      }
    }
    return echoInstance;
  }

  // Method to bind common Pusher connection events
  private handleConnectionEvents(echoInstance: any, conversationId?: number) {
    const pusher = echoInstance?.connector?.pusher;
    if (!pusher) {
      return;
    }

    pusher.connection.bind('connected', () => {});

    pusher.connection.bind('disconnected', () => {
      if (conversationId !== undefined) {
        this.tryReconnect(conversationId);
      }
    });

    pusher.connection.bind('reconnecting', () => {});

    pusher.connection.bind('failed', () => {});
  }

  // Unified reconnection logic
  private tryReconnect(conversationId?: number, maxAttempts: number = 5, delay: number = 2000) {
    if (conversationId === undefined) {
      return;
    }

    let attempts = 0;
    const reconnectInterval = setInterval(() => {
      const echoInstance = this.getEchoInstance();
      if (!echoInstance) {
        clearInterval(reconnectInterval);
        return;
      }

      const pusher = echoInstance.connector.pusher;
      if (pusher && pusher.connection.state === 'connected') {
        this.removeListeners(conversationId); // Ensure all listeners are cleaned up
        this.listenToNewMessage(conversationId); // Re-subscribe
        clearInterval(reconnectInterval);
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(reconnectInterval);
        }
      }
    }, delay);
  }

  // Extract common channel subscription logic
  private subscribeToChannel(channelName: string, event: string, subject: Subject<any>, conversationId?: number) {
    const echoInstance = this.getEchoInstance();
    if (!echoInstance) return;

    const listenerKey = `${channelName}-${event}`;
    if (this.activeListeners.has(listenerKey)) {
      return;
    }

    const channel = echoInstance.private(channelName);
    channel.listen(event, (e: any) => subject.next(e));
    this.activeListeners.add(listenerKey);

    if (conversationId !== undefined) {
      this.handleConnectionEvents(echoInstance, conversationId);
    }
  }

  listenIfUserHasBeenRemovedFromChat(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.ParticipantRemovedFromConversation', this.hasUserBeenRemoved, conversationId);
  }

  listenToNewMessage(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.ConversationNewMessage', this.newMessageSubject, conversationId);
  }

  listenToNewConversation(userId: number): void {
    const channelName = `App.Models.User.${userId}`;
    this.subscribeToChannel(channelName, '.NewConversation', this.newChatSubject);
  }

  listenIfSomeoneLeftConversation(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.ParticipantLeftTheConversation', this.userLeft, conversationId);
  }

  listenForRefreshingList(userId: number): void {
    const channelName = `App.Models.User.${userId}`;
    const echoInstance = this.getEchoInstance();
    if (!echoInstance) return;

    if (!this.channels.has(userId)) {
      const channel = echoInstance.private(channelName);
      this.channels.set(userId, channel);

      channel.listen('.RefreshConversations', (e: any) => {
        this.refreshListSubject.next(e);
      });
    }
  }

  listenToEmojiReaction(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.MessageReaction', this.newEmojiReaction, conversationId);
  }

  listenIfEmojiReactionRemoved(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.MessageReactionDeleted', this.deleteEmojiReaction, conversationId);
  }

  listenMessageEdit(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.MessageUpdated', this.messageUpdated, conversationId);
  }

  listenMessageDelete(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.MessageDeleted', this.messageDeleted, conversationId);
  }

  listenForGroupDetailsChanges(conversationId: number): void {
    const channelName = `conversations.${conversationId}`;
    this.subscribeToChannel(channelName, '.ConversationUpdated', this.groupDetailsChanges, conversationId);
  }

  listenToNotifications(userId: number): void {
    const channelName = WebsocketEnums.notificationsChannelName + userId;
    const echoInstance = this.getEchoInstance();
    if (!echoInstance) return;
    const channel = echoInstance.private(channelName);
    channel.notification((e: any) => {
      this.notificationReceivedSubject.next(e);
    });
  }

  disconnectFromNotificationsChannel(userId: number): void {
    const echoInstance = this.getEchoInstance();
    if (!echoInstance) return;
    echoInstance.leaveChannel(WebsocketEnums.notificationsChannelName + userId);
  }

  // Cleanup all listeners for a conversation channel
  private removeListeners(conversationId: number): void {
    const echoInstance = this.getEchoInstance();
    if (!echoInstance) return;

    const channelName = `conversations.${conversationId}`;
    const channel = echoInstance.private(channelName);

    if (channel) {
      // Only stop listening to conversation-specific events
      channel.stopListening('.ConversationNewMessage');
      channel.stopListening('.MessageReaction');
      channel.stopListening('.MessageReactionDeleted');
      channel.stopListening('.ParticipantLeftTheConversation');
      channel.stopListening('.MessageDeleted');
      channel.stopListening('.MessageUpdated');
    }

    // Remove from activeListeners
    this.activeListeners.forEach(listenerKey => {
      if (listenerKey.startsWith(channelName)) {
        this.activeListeners.delete(listenerKey);
      }
    });
  }

  cleanupChannel(userId: number) {
    const channel = this.channels.get(userId);
    if (channel) {
      channel.stopListening('.ConversationNewMessage');
      channel.stopListening('.MessageReaction');
      channel.stopListening('.MessageReactionDeleted');
      channel.stopListening('.ParticipantLeftTheConversation');
      channel.stopListening('.MessageDeleted');
      channel.stopListening('.MessageUpdated');
      channel.stopListening('.RefreshConversations');
      channel.stopListening('.NewConversation');
      this.channels.delete(userId);
    }
  }
}
